// ============================================================================
// Campaigns — reporting UI (read-only). Talks to GET /api/campaigns.
//   Default: list of campaigns with per-campaign delivery counts.
//   Detail : one campaign's rollup stat-row + recipient message table.
// Uses only existing shared classes (card/pad, mod-h, table-wrap/h-table,
// stat-row/stat, pill, btn ghost, empty, msg) — inline style="" for tweaks only.
// ============================================================================
import { api } from '../lib/api.js'
import { esc } from '../lib/view.js'
import { fmtPhone, etDT, usd, pct } from '../lib/format.js'
import { errorInfo } from '../lib/errorcodes.js'

const nf = (n) => Number(n || 0).toLocaleString('en-US')
// cost_micro (micro-dollars) → usd() which wants cents: micro / 10000 = cents.
const cost = (micro) => usd(Number(micro || 0) / 10000)

// Status → pill class. Delivered = success, failed/undelivered = bad (marketing
// palette reused for red), sent/queued = neutral.
function statusPill(s) {
  const st = String(s || '').toLowerCase()
  if (st === 'delivered') return `<span class="pill an">${esc(st)}</span>`
  if (st === 'failed' || st === 'undelivered') return `<span class="pill mk">${esc(st)}</span>`
  return `<span class="pill">${esc(st || '—')}</span>`
}

export function renderCampaigns(el) {
  showList(el)
}

async function showList(el) {
  el.innerHTML = `
    <div class="card pad">
      <div class="mod-h">Campaigns</div>
      <div class="mod-sub">Every send is a campaign of record. Click a row for per-recipient detail.</div>
      <div class="toolbar">
        <span class="grow"></span>
        <select class="h-input" id="cpSize" style="width:auto">
          <option value="25">25 / page</option>
          <option value="50">50 / page</option>
          <option value="100">100 / page</option>
        </select>
        <button class="btn ghost" id="cpExport">Export CSV</button>
      </div>
      <div class="table-wrap">
        <table class="h-table">
          <thead><tr>
            <th>Name</th><th>Created</th><th class="num">Sent</th><th class="num">Delivered</th>
            <th class="num">Failed</th><th class="num">Rate</th><th>Status</th>
          </tr></thead>
          <tbody id="cpRows"><tr><td colspan="7" class="empty">Loading campaigns…</td></tr></tbody>
        </table>
      </div>
      <div class="toolbar" style="margin-top:12px;margin-bottom:0">
        <span class="mod-sub" id="cpCount" style="margin:0"></span>
        <span class="grow"></span>
        <button class="btn ghost" id="cpPrev">‹ Prev</button>
        <span class="mod-sub" id="cpPage" style="margin:0"></span>
        <button class="btn ghost" id="cpNext">Next ›</button>
      </div>
    </div>`

  const $ = (id) => el.querySelector('#' + id)
  const rows = $('cpRows')

  // ---- pagination state ----
  let pageSize = 25, page = 0, total = 0

  async function load() {
    rows.innerHTML = '<tr><td colspan="7" class="empty">Loading campaigns…</td></tr>'
    try {
      const r = await api.get(`/api/campaigns?limit=${pageSize}&offset=${page * pageSize}`)
      const campaigns = r.campaigns || []
      total = r.total || 0
      if (!campaigns.length) {
        rows.innerHTML = '<tr><td colspan="7" class="empty">No campaigns yet.</td></tr>'
        updatePager(0)
        return
      }
      rows.innerHTML = campaigns.map((c) => `
        <tr data-id="${esc(c.id)}" style="cursor:pointer">
          <td>${esc(c.name)}</td>
          <td class="num">${c.created_at ? esc(etDT(c.created_at)) : '—'}</td>
          <td class="num">${nf(c.total)}</td>
          <td class="num">${nf(c.delivered)}</td>
          <td class="num">${nf(c.failed)}</td>
          <td class="num">${esc(c.rate)}%</td>
          <td>${statusPill(c.status)}</td>
        </tr>`).join('')
      rows.querySelectorAll('tr[data-id]').forEach((tr) =>
        tr.addEventListener('click', () => showDetail(el, tr.dataset.id)))
      updatePager(campaigns.length)
    } catch (e) {
      rows.innerHTML = `<tr><td colspan="7" class="empty">Could not load campaigns: ${esc(e.message)}</td></tr>`
    }
  }

  function updatePager(shown) {
    const from = total === 0 ? 0 : page * pageSize + 1
    const to = page * pageSize + shown
    const pages = Math.max(Math.ceil(total / pageSize), 1)
    $('cpCount').textContent = `Showing ${from}–${to} of ${total}`
    $('cpPage').textContent = `Page ${page + 1} of ${pages}`
    $('cpPrev').disabled = page <= 0
    $('cpNext').disabled = page >= pages - 1
    $('cpPrev').style.opacity = page <= 0 ? '.5' : '1'
    $('cpNext').style.opacity = page >= pages - 1 ? '.5' : '1'
  }

  // ---- CSV export (all campaigns) ----
  function toCsv(list) {
    const cols = ['name', 'created', 'sent', 'delivered', 'failed', 'rate', 'status']
    const val = (c, col) =>
      col === 'created' ? (c.created_at ? new Date(c.created_at).toISOString() : '')
      : col === 'sent' ? String(c.total ?? 0)
      : col === 'rate' ? c.rate + '%'
      : c[col] == null ? '' : String(c[col])
    const cell = (c, col) => {
      let v = val(c, col)
      if (/^[=+\-@\t\r]/.test(v)) v = "'" + v; return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v
    }
    return [cols.join(','), ...list.map(c => cols.map(col => cell(c, col)).join(','))].join('\n')
  }

  $('cpExport').addEventListener('click', async () => {
    const btn = $('cpExport'); const label = btn.textContent; btn.textContent = 'Exporting…'; btn.disabled = true
    try {
      const r = await api.get('/api/campaigns?limit=100000')
      const csv = toCsv(r.campaigns || [])
      const blob = new Blob([csv], { type: 'text/csv' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `hive-campaigns-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(a.href)
    } catch (e) { alert('Export failed: ' + e.message) }
    finally { btn.textContent = label; btn.disabled = false }
  })

  $('cpSize').addEventListener('change', () => { pageSize = parseInt($('cpSize').value) || 25; page = 0; load() })
  $('cpPrev').addEventListener('click', () => { if (page > 0) { page--; load() } })
  $('cpNext').addEventListener('click', () => { page++; load() })

  load()
}

async function showDetail(el, id) {
  el.innerHTML = `
    <div class="card pad">
      <div class="toolbar">
        <div class="grow"><div class="mod-h" id="cpTitle">Campaign</div><div class="mod-sub" id="cpMeta">Loading…</div></div>
        <button class="btn ghost" id="cpExportRecips" disabled>Export recipients</button>
        <button class="btn ghost" id="cpBack">← Back</button>
      </div>
      <div id="cpBody"><div class="msg">Loading campaign…</div></div>
    </div>`

  el.querySelector('#cpBack').addEventListener('click', () => showList(el))
  const body = el.querySelector('#cpBody')

  try {
    const { campaign, messages = [], rollup = {} } = await api.get('/api/campaigns?id=' + encodeURIComponent(id))
    el.querySelector('#cpTitle').textContent = campaign?.name || 'Campaign'
    const by = campaign?.created_by_name ? ' · by ' + campaign.created_by_name : ''
    const when = campaign?.created_at ? etDT(campaign.created_at) : '—'
    el.querySelector('#cpMeta').textContent =
      `${campaign?.use_case || 'campaign'} · created ${when}${by}`
    body.innerHTML = statRow(rollup) + recipientTable(messages)

    // Export the full recipient list for the product team to look up backend results.
    const exp = el.querySelector('#cpExportRecips')
    if (messages.length) {
      exp.disabled = false
      exp.addEventListener('click', () => exportRecipients(campaign, messages))
    } else {
      exp.title = 'No recipients recorded'
    }
  } catch (e) {
    body.innerHTML = `<div class="msg bad">Could not load this campaign: ${esc(e.message)}</div>`
  }
}

// Recipient CSV — raw phone (machine-matchable), status, reason, provider ids,
// sending number, cost, ISO sent time. This is what goes to the product team.
function exportRecipients(campaign, messages) {
  const cols = ['phone', 'status', 'reason', 'error_code', 'provider', 'provider_id', 'from_number', 'cost_usd', 'sent_at']
  const esc2 = (v) => { let s = v == null ? '' : String(v); if (/^[=+\-@\t\r]/.test(s)) s = "'" + s; return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s }
  const rows = messages.map((m) => [
    m.to_phone || '',
    m.status || '',
    m.error_code ? errorInfo(m.error_code).label : '',
    m.error_code || '',
    m.provider || '',
    m.provider_id || '',
    m.from_number || '',
    m.cost_micro != null ? (Number(m.cost_micro) / 1e6).toFixed(4) : '',
    m.sent_at ? new Date(m.sent_at).toISOString() : '',
  ].map(esc2).join(','))
  const csv = [cols.join(','), ...rows].join('\n')
  const slug = String(campaign?.name || 'campaign').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'campaign'
  const blob = new Blob([csv], { type: 'text/csv' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = `hive-recipients-${slug}-${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(a); a.click(); a.remove()
  URL.revokeObjectURL(a.href)
}

function stat(k, v, d) {
  return '<div class="stat"><div class="k">' + esc(k) + '</div><div class="v">' + esc(v) + '</div>' +
    (d ? '<div class="d">' + esc(d) + '</div>' : '') + '</div>'
}

function statRow(r) {
  // Prefer the server-computed rate; fall back to pct() if absent.
  const rate = (r.rate != null ? r.rate : pct(r.delivered, Number(r.delivered || 0) + Number(r.failed || 0)))
  const clicks = r.clicks != null ? ' · ' + nf(r.clicks) + ' clicks' : ''
  return '<div class="stat-row" style="margin-bottom:16px">' +
    stat('Delivery rate', rate + '%', 'of attempted') +
    stat('Sent', nf(r.total)) +
    stat('Delivered', nf(r.delivered)) +
    stat('Failed', nf(r.failed), 'incl. undelivered') +
    stat('Opt-outs / spam', nf(r.spamBlocked), 'error 607') +
    stat('Cost', cost(r.costMicro), 'total' + clicks) +
    '</div>'
}

// A failure's reason: only meaningful on non-delivered rows. Shows the human
// reason with the full description on hover; delivered/sent rows show "—".
function reasonCell(m) {
  if (!m.error_code) return '—'
  const info = errorInfo(m.error_code)
  const cls = info.spam ? ' style="color:var(--pink)"' : ''
  return `<span title="${esc(info.code + ' · ' + info.desc)}"${cls}>${esc(info.label)}</span>`
}

function recipientTable(messages) {
  if (!messages.length) return '<div class="empty">No recipients recorded for this campaign.</div>'
  const rows = messages.map((m) => `
    <tr>
      <td class="num">${esc(fmtPhone(m.to_phone))}</td>
      <td>${statusPill(m.status)}</td>
      <td>${reasonCell(m)}</td>
      <td class="num">${m.cost_micro != null ? esc(cost(m.cost_micro)) : '—'}</td>
      <td class="num">${m.sent_at ? esc(etDT(m.sent_at)) : '—'}</td>
    </tr>`).join('')
  return '<div class="mod-h" style="margin-top:6px">Recipients</div>' +
    '<div class="table-wrap"><table class="h-table"><thead><tr>' +
    '<th>Phone</th><th>Status</th><th>Reason</th><th class="num">Cost</th><th class="num">Sent at</th>' +
    '</tr></thead><tbody>' + rows + '</tbody></table></div>'
}
